import { useEffect } from "react";
import { Col, FormGroup, FormLabel, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import AsteriskRequired from "../../../components/Common/AsteriskRequired";
import StatusCheckBox from "../../../components/Common/StatusCheckbox";
import FieldValidationError from "../../../components/Forms/Element/FieldValidationError";
import EmployeeSelect from "../../../components/Custom/Main/Employee/EmployeeSelect";
import PerformancePlanSelect from "../../../components/Custom/Performance/PerformancePlanSelect";
import { convertStatusToBoolean } from "../../../../utils/ConverterUtils";
import { useBranch } from "../../../../context/BranchContext";
import FormSection from "../../../components/Common/Form/FormSection";

const PerformancePlanEmployeeForm = ({ formik, status, setStatus, entityData }) => {

    const { t } = useTranslation();
    const { selectedBranch } = useBranch();
    const isEditing = !!entityData;
    const employeeSelectValue = isEditing
        ? (formik.values.employees?.[0] || null)
        : formik.values.employees;

    const handleEmployeeChange = (value) => {
        formik.setFieldValue('employees', isEditing ? (value ? [value] : []) : (value || []));
    };

    useEffect(() => {
        if (entityData != null) {
            if (entityData.employee) {
                formik.setFieldValue('employees', [{ value: entityData.employee.id, label: entityData.employee.fullName }]);
            }
            if (entityData.plan) {
                formik.setFieldValue('plan', { value: entityData.plan.id, label: entityData.plan.name });
            }
            formik.setFieldValue('startDate', entityData.startDate || "");
            formik.setFieldValue('endDate', entityData.endDate || "");
            setStatus(convertStatusToBoolean(entityData.status));
        }
        // eslint-disable-next-line
    }, [entityData]);

    useEffect(() => {
        if (!isEditing) {
            formik.setFieldValue('employees', []);
        }
        // eslint-disable-next-line
    }, [selectedBranch?.id]);

    return (
        <div className="form-layout form-layout--spaced">
            <FormSection
                title={t('basic_information')}
                description={t('performance.employee_plan_hint')}
                icon="fa-solid fa-user-check"
            >

                <Row className="g-3">
                    <Col md={6}>
                        <FormGroup>
                            <FormLabel>{t('employee')} <AsteriskRequired /></FormLabel>
                            <EmployeeSelect
                                value={employeeSelectValue}
                                onChange={handleEmployeeChange}
                                branch={selectedBranch}
                                isMulti={!isEditing}
                                includeSelectAll={!isEditing}
                            />
                            <FieldValidationError error={formik.errors.employees} />
                            {selectedBranch && (
                                <small className="text-muted">
                                    {t('branch')}: {selectedBranch.name}
                                </small>
                            )}
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <FormLabel>{t('performance.plan')} <AsteriskRequired /></FormLabel>
                            <PerformancePlanSelect value={formik.values.plan} onChange={(value) => formik.setFieldValue('plan', value)} />
                            <FieldValidationError error={formik.errors.plan} />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <FormLabel>{t('start_date')} <AsteriskRequired /></FormLabel>
                            <input className="form-control" type="date" name="startDate" value={formik.values.startDate} onChange={formik.handleChange} />
                            <FieldValidationError error={formik.errors.startDate} />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <FormLabel>{t('end_date')}</FormLabel>
                            <input className="form-control" type="date" name="endDate" value={formik.values.endDate} onChange={formik.handleChange} />
                        </FormGroup>
                    </Col>
                </Row>
            </FormSection>

            <FormSection
                title={t('status')}
                description={t('performance.employee_plan_status_hint')}
                icon="fa-solid fa-toggle-on"
                compact
            >

                <Row>
                    <Col md={12}>
                        <FormGroup className="mb-0">
                            <StatusCheckBox
                                name="status"
                                defaultChecked={status}
                                checked={status}
                                onClick={() => setStatus(!status)}
                            />
                        </FormGroup>
                    </Col>
                </Row>
            </FormSection>
        </div>
    );
}

export default PerformancePlanEmployeeForm;
